import React, { lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from 'sonner';
import AuthPage from './pages/AuthPage';
import PendingPage from './pages/PendingPage';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const VesselDetailPage = lazy(() => import('./pages/VesselDetailPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PublicActionPage = lazy(() => import('./pages/PublicActionPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PreInspectionPage = lazy(() => import('./pages/PreInspectionPage'));
const VesselPrepSummaryPage = lazy(() => import('./pages/VesselPrepSummaryPage'));
const FleetIntelligencePage = lazy(() => import('./pages/FleetIntelligencePage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex gap-2">{[0,1,2,3].map(i=><div key={i} className="w-2.5 h-2.5 rounded-full bg-navy-700 animate-dot-pulse" style={{animationDelay:`${i*0.18}s`}}/>)}</div>
  </div>
);
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading, isVesselUser, isPending } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex gap-2">{[0,1,2,3].map(i=><div key={i} className="w-2.5 h-2.5 rounded-full bg-navy-700 animate-dot-pulse" style={{animationDelay:`${i*0.18}s`}}/>)}</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" />;

  // PENDING / PENDING_ADMIN users — only if manually flagged by Super Admin
  if (isPending && window.location.pathname !== '/pending') {
    return <Navigate to="/pending" />;
  }
  
  // VESSEL users auto-redirect to their assigned vessel
  if (isVesselUser && user.assigned_vessel_id && window.location.pathname === '/dashboard') {
    return <Navigate to={`/vessel/${user.assigned_vessel_id}`} />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex gap-2">{[0,1,2,3].map(i=><div key={i} className="w-2.5 h-2.5 rounded-full bg-navy-700 animate-dot-pulse" style={{animationDelay:`${i*0.18}s`}}/>)}</div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              }
            />
            <Route path="/invite/:token" element={<AuthPage />} />
            <Route path="/action/:token" element={<PublicActionPage />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/vessel/:vesselId"
              element={
                <PrivateRoute>
                  <VesselDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <PrivateRoute>
                  <BillingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pending"
              element={
                <PrivateRoute>
                  <PendingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <ProfilePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pre-inspection"
              element={
                <PrivateRoute>
                  <PreInspectionPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/vessel/:vesselId/prep-summary"
              element={
                <PrivateRoute>
                  <VesselPrepSummaryPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/fleet-intelligence"
              element={
                <PrivateRoute>
                  <FleetIntelligencePage />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <div className="text-center p-8">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
        <p className="text-gray-500 mb-4">Our team has been notified. Please refresh the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-navy-700 text-white rounded-lg text-sm font-medium"
        >
          Refresh
        </button>
      </div>
    </div>
  ),
});
